// ============================================================================
// crmSom · o som baixo de mensagem nova de cliente no CRM (28/10/2026, CRM_SOM)
// ============================================================================
// Pedido do dono: "um barulhinho quando chega mensagem nova de cliente, baixo,
// um barulho baixo, por favor". Dois toques curtos e suaves (ré e lá, onda
// triangular, filtro passa-baixa em 2400 Hz, ~0,45 s), feitos na hora pelo
// WebAudio: nada é baixado.
//   · nada de AudioContext ao importar: ele nasce no primeiro toque ou tecla
//     dentro do CRM (destravar, o que o iPhone exige), nunca sozinho; a cada
//     gesto, se o iOS suspendeu o contexto, ele é destravado de novo;
//   · no máximo um a cada 3 s (podeTocar);
//   · com várias abas do CRM, só a última que ficou em foco toca;
//   · contexto suspenso ou travado: silêncio (nunca erro na tela).
// 30/10/2026 (CRM_VIGIA; pedido do dono: "o som também não está funcionando"): o pico
// do padrão (0,25) era -29 dBFS (ganho 0,12 x 0,25, quase inaudível) e passa a ser
// perto de -14 dBFS (ganho 0,7 x volume); o volume 1 fica em 0,77 de pico, nunca acima
// de 0,9 (picoDoVolume faz a conta pelo mesmo envelope dos dois toques). O vigia
// (VigiaCrm) prende o desbloqueio no documento inteiro (qualquer clique ou tecla no
// DNA, não só dentro do CRM) e toca pelo tocarAviso, que diz por que não tocou:
// 'travado' (depois de uma recarga o navegador só libera o áudio com um gesto) faz
// aparecer o aviso pequeno "Clique aqui para ligar o som do CRM". Ao importar, ainda
// nada de AudioContext: com o vigia ele também nasce quando um toque é devido (sem
// gesto ele fica suspenso, e o próximo clique destrava). false = como antes.
// ============================================================================

import { CRM_VIGIA } from '../config/featureFlags.js';

export const INTERVALO_MIN_MS = 3000;
export const CHAVE_DONO = 'fk.crm.somDono';

// ── o volume: o envelope dos dois toques e o ganho de cada um ──
export const GANHO_ANTES = 0.12;    // até 29/10/2026: 0,25 → pico de -29 dBFS
export const GANHO_VIGIA = 0.7;     // 30/10/2026: 0,25 → pico perto de -14 dBFS; 1 → 0,77
export const PICO_MAX = 0.9;        // o volume 1 nunca passa disso (sem estourar)
export const ATAQUE_S = 0.008;
export const DECAIMENTO_S = 0.35;
export const PISO = 0.0001;
export const ATRASO_SEGUNDO_S = 0.09;
/** Com a conversa aberta e à vista (ela já está vendo), o som sai mais baixo: o fator do volume. */
export const FATOR_CONVERSA_ABERTA = CRM_VIGIA ? 0.7 : 0.5;

/** O ganho de cada toque para um volume 0..1. */
export function ganhoDoVolume(volume, { vigia = CRM_VIGIA } = {}) {
  const v = Math.max(0, Math.min(1, Number(volume) || 0));
  return (vigia ? GANHO_VIGIA : GANHO_ANTES) * v;
}
/**
 * O pico da soma dos dois toques (0..1 de escala cheia): o primeiro no topo mais o que sobra do
 * primeiro quando o segundo chega ao topo (a descida exponencial até o PISO), no pior encaixe de fase.
 */
export function picoDoVolume(volume, opcoes = {}) {
  const g = ganhoDoVolume(volume, opcoes);
  if (!g) return 0;
  const sobra = g * (PISO / g) ** (ATRASO_SEGUNDO_S / (DECAIMENTO_S - ATAQUE_S));
  return g + sobra;
}
export const dBFS = (pico) => (pico > 0 ? 20 * Math.log10(pico) : Number.NEGATIVE_INFINITY);

let ctx = null;
let ultimoMs = 0;
const ouvintes = new Set();

/** Um toque no máximo a cada 3 s. */
export function podeTocar(ultimo, agoraMs) {
  return !(Number(agoraMs) - Number(ultimo || 0) < INTERVALO_MIN_MS);
}

/** O contexto de áudio, criado só quando alguém precisa (nunca ao importar). */
export function garantirContexto() {
  if (ctx) return ctx;
  try {
    const AC = typeof window !== 'undefined' ? (window.AudioContext || window.webkitAudioContext) : null;
    if (!AC) return null;
    ctx = new AC();
    // quem espera o áudio destravar (o aviso "ligar o som" do vigia some sozinho)
    const c = ctx;
    c.addEventListener?.('statechange', () => { for (const f of [...ouvintes]) { try { f(c.state); } catch { /* segue */ } } });
  } catch { ctx = null; }
  return ctx;
}

/** O áudio está liberado (contexto rodando)? */
export function somDestravado() { return !!ctx && ctx.state === 'running'; }
/** Avisa quando o contexto muda de estado ('running' = destravou). Devolve o desligar. */
export function ouvirSom(fn) { ouvintes.add(fn); return () => { ouvintes.delete(fn); }; }

/** O desbloqueio (gesto da pessoa): retoma o contexto e toca 1 amostra muda (o truque do iPhone). */
export function destravar() {
  const c = garantirContexto();
  if (!c) return false;
  try {
    // "suspended" (Chrome, antes do gesto) e "interrupted" (iOS, tela bloqueada ou outro app): retoma
    if (c.state !== 'running' && c.state !== 'closed' && typeof c.resume === 'function') c.resume().catch(() => {});
    const b = c.createBuffer(1, 1, 22050);
    const s = c.createBufferSource();
    s.buffer = b;
    s.connect(c.destination);
    s.start(0);
  } catch { /* sem áudio */ }
  return true;
}

/**
 * Prende o desbloqueio nos toques e teclas dentro do CRM. Devolve o desligar.
 * Revisão de 28/10/2026: no iPhone o pointerdown de um TOQUE não conta como gesto (o navegador só
 * libera o áudio no touchend, pointerup ou click), e o iOS volta o contexto para "interrupted" ou
 * "suspended" depois de bloquear a tela ou trocar de app. Por isso escuta os eventos que valem como
 * gesto em cada aparelho e fica preso: a cada gesto, se o contexto não está "running", destrava de novo.
 */
export const EVENTOS_DE_GESTO = Object.freeze(['pointerup', 'touchend', 'click', 'keydown', 'pointerdown']);
export function prenderDestravar(el) {
  if (!el || typeof el.addEventListener !== 'function') return () => {};
  const f = () => { if (!ctx || ctx.state !== 'running') destravar(); };
  const op = { capture: true, passive: true };
  for (const ev of EVENTOS_DE_GESTO) el.addEventListener(ev, f, op);
  return () => { for (const ev of EVENTOS_DE_GESTO) el.removeEventListener(ev, f, op); };
}

/** O "Testar som" da Aparência: destrava (dentro do clique) e toca quando o contexto estiver rodando. */
export function testarSom(volume = 0.25) {
  const c = garantirContexto();
  if (!c) return false;
  destravar();
  const tocarJa = () => { tocarChime(volume); };
  try {
    if (c.state === 'running') tocarJa();
    else if (typeof c.resume === 'function') c.resume().then(tocarJa).catch(() => {});
  } catch { return false; }
  return true;
}

/** Os dois toques. volume 0..1 (o padrão da Aparência é 0,25). Sem contexto destravado: nada. */
export function tocarChime(volume = 0.25) {
  const c = ctx;
  if (!c || c.state !== 'running') return false;
  const v = Math.max(0, Math.min(1, Number(volume) || 0));
  if (!v) return false;
  try {
    const t0 = c.currentTime + 0.01;
    const filtro = c.createBiquadFilter();
    filtro.type = 'lowpass';
    filtro.frequency.value = 2400;
    filtro.connect(c.destination);
    for (const [freq, atraso] of [[587.33, 0], [880, ATRASO_SEGUNDO_S]]) {
      const o = c.createOscillator();
      const g = c.createGain();
      o.type = 'triangle';
      o.frequency.value = freq;
      const t = t0 + atraso;
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(ganhoDoVolume(v), t + ATAQUE_S);
      g.gain.exponentialRampToValueAtTime(PISO, t + DECAIMENTO_S);
      o.connect(g);
      g.connect(filtro);
      o.start(t);
      o.stop(t + 0.37);
    }
    return true;
  } catch { return false; }
}

// ── várias abas: só a última em foco toca ──
const lerDono = () => { try { return window.localStorage.getItem(CHAVE_DONO); } catch { return null; } };
const gravarDono = (id) => { try { window.localStorage.setItem(CHAVE_DONO, id); } catch { /* sem armazenamento: toca a aba visível */ } };

/** Registra esta aba (vira a dona ao ganhar foco ou ficar visível). Devolve { id, parar }. */
export function registrarAba() {
  const id = `t${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
  if (typeof window === 'undefined') return { id, parar: () => {} };
  const tomar = () => { if (typeof document === 'undefined' || document.visibilityState === 'visible') gravarDono(id); };
  tomar();
  window.addEventListener('focus', tomar);
  document.addEventListener('visibilitychange', tomar);
  return { id, parar: () => { window.removeEventListener('focus', tomar); document.removeEventListener('visibilitychange', tomar); } };
}

/** Esta aba pode tocar: está visível, ou foi a última do CRM em foco. */
export function abaPodeTocar(id) {
  if (typeof document !== 'undefined' && document.visibilityState === 'visible') return true;
  return !!id && lerDono() === id;
}

/** O toque da Caixa: respeita a aba, o intervalo e o volume. */
export function tocarMensagemNova({ volume = 0.25, abaId = null, agoraMs = Date.now() } = {}) {
  return tocarMensagemNovaComMotivo({ volume, abaId, agoraMs }) === 'tocou';
}
/** O mesmo toque, dizendo o que houve: 'tocou' | 'aba' (outra aba toca) | 'intervalo' (tocou há menos de 3 s) | 'mudo' | 'travado'. */
export function tocarMensagemNovaComMotivo({ volume = 0.25, abaId = null, agoraMs = Date.now() } = {}) {
  if (!abaPodeTocar(abaId)) return 'aba';
  if (!podeTocar(ultimoMs, agoraMs)) return 'intervalo';
  if (!(Math.max(0, Math.min(1, Number(volume) || 0)))) return 'mudo';
  const ok = tocarChime(volume);
  if (ok) ultimoMs = agoraMs;
  return ok ? 'tocou' : 'travado';
}

/**
 * O toque do vigia (30/10/2026, CRM_VIGIA): quem decide que toca é o vigia (crmVigia.decidirAviso), aqui só
 * o intervalo de 3 s e o volume. Sem o áudio liberado, tenta retomar (alguns navegadores deixam sem gesto);
 * não deu em esperaMs: 'travado' (o vigia mostra "Clique aqui para ligar o som do CRM").
 * → 'tocou' | 'intervalo' | 'mudo' | 'travado'
 */
export async function tocarAviso({ volume = 0.25, agoraMs = Date.now(), esperaMs = 400 } = {}) {
  if (!(Math.max(0, Math.min(1, Number(volume) || 0)))) return 'mudo';
  if (!podeTocar(ultimoMs, agoraMs)) return 'intervalo';
  const c = ctx && ctx.state === 'running' ? ctx : garantirContexto();
  if (!c) return 'travado';
  if (c.state !== 'running' && c.state !== 'closed' && typeof c.resume === 'function') {
    let t = null;
    try { await Promise.race([Promise.resolve(c.resume()).catch(() => {}), new Promise((r) => { t = setTimeout(r, esperaMs); })]); } catch { /* segue travado */ } finally { clearTimeout(t); }
  }
  if (c.state !== 'running') return 'travado';
  if (!podeTocar(ultimoMs, agoraMs)) return 'intervalo';
  const ok = tocarChime(volume);
  if (ok) ultimoMs = agoraMs;
  return ok ? 'tocou' : 'travado';
}

/** Só para os testes. */
export function _zerarSom() { ctx = null; ultimoMs = 0; ouvintes.clear(); }
